import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaApple, FaGooglePlay } from 'react-icons/fa6';
import { HiXMark } from 'react-icons/hi2';

import classes from './styles.module.css';
import Logo from 'src/components/logo/Logo';
import { ANDROID_APP_URL, IOS_APP_URL } from 'src/constants/vars';
import { AppPromoChoice, rememberAppPromoChoice, shouldShowAppPromo } from 'src/utils/appPromo';

/** Small delay so the modal lands after the page paints, not on top of it. */
const OPEN_DELAY_MS = 800;

/**
 * Only xs/sm/md get the prompt — below the `lg` breakpoint the site uses
 * everywhere else. Pitching a phone app to someone already on a desktop is
 * noise, and the store links would open on the wrong device anyway.
 */
const SMALL_SCREEN_QUERY = '(max-width: 991px)';

const isSmallScreenNow = (): boolean => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
        return window.matchMedia(SMALL_SCREEN_QUERY).matches;
    }
    // jsdom and very old browsers have no matchMedia; fall back to the width.
    return window.innerWidth <= 991;
};

const AppPromoModal = () => {
    const { t } = useTranslation(['components']);
    const [open, setOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(isSmallScreenNow);
    const dialogRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    /** Restores focus to wherever the visitor was when the modal stole it. */
    const previousFocus = useRef<Element | null>(null);

    const hasAndroid = Boolean(ANDROID_APP_URL);
    const hasIOS = Boolean(IOS_APP_URL);
    const hasAnyApp = hasAndroid || hasIOS;

    // Follow the breakpoint live, so resizing across it behaves sensibly in both
    // directions rather than only at first paint.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

        const query = window.matchMedia(SMALL_SCREEN_QUERY);
        const sync = () => setIsSmallScreen(query.matches);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        // Nothing to promote, or a screen we do not promote on, means nothing
        // to interrupt for.
        if (!hasAnyApp || !isSmallScreen || !shouldShowAppPromo()) return;

        const timer = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [hasAnyApp, isSmallScreen]);

    const dismiss = useCallback((choice: AppPromoChoice) => {
        rememberAppPromoChoice(choice);
        setOpen(false);
    }, []);

    /**
     * Growing past the breakpoint hides the modal without recording a choice,
     * so a later visit on a phone still gets the prompt.
     */
    const visible = open && isSmallScreen;

    // Lock the page behind the modal, move focus in, and put it back on close.
    useEffect(() => {
        if (!visible) return;

        previousFocus.current = document.activeElement;
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();

        return () => {
            document.body.style.overflow = overflow;
            (previousFocus.current as HTMLElement | null)?.focus?.();
        };
    }, [visible]);

    // Escape closes, Tab cycles inside the dialog.
    useEffect(() => {
        if (!visible) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                dismiss('web');
                return;
            }

            if (event.key !== 'Tab') return;

            const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button');
            if (!focusables || focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [visible, dismiss]);

    if (!visible) return null;

    return (
        <div
            className={classes.overlay}
            onClick={() => dismiss('web')}
            data-testid="app-promo-overlay"
        >
            <div
                ref={dialogRef}
                className={classes.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="app-promo-title"
                aria-describedby="app-promo-msg"
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    ref={closeRef}
                    type="button"
                    className={classes.close}
                    onClick={() => dismiss('web')}
                    aria-label={t('app_promo.close')}
                >
                    <HiXMark aria-hidden="true" />
                </button>

                <div className={classes.brand} aria-hidden="true">
                    <Logo />
                </div>

                <h2 id="app-promo-title" className={classes.title}>
                    {t('app_promo.title')}
                </h2>
                <p id="app-promo-msg" className={classes.message}>
                    {t('app_promo.msg')}
                </p>

                <div className={classes.actions}>
                    {hasAndroid ? (
                        <a
                            href={ANDROID_APP_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={`${classes.storeBtn} ${classes.android}`}
                            onClick={() => dismiss('android')}
                        >
                            <FaGooglePlay aria-hidden="true" />
                            <span className={classes.storeText}>
                                <span className={classes.storeHint}>{t('app_promo.get_it_on')}</span>
                                <span className={classes.storeName}>{t('app_promo.android')}</span>
                            </span>
                        </a>
                    ) : null}

                    {hasIOS ? (
                        <a
                            href={IOS_APP_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={`${classes.storeBtn} ${classes.ios}`}
                            onClick={() => dismiss('ios')}
                        >
                            <FaApple aria-hidden="true" />
                            <span className={classes.storeText}>
                                <span className={classes.storeHint}>{t('app_promo.download_on')}</span>
                                <span className={classes.storeName}>{t('app_promo.ios')}</span>
                            </span>
                        </a>
                    ) : null}
                </div>

                <button type="button" className={classes.continue} onClick={() => dismiss('web')}>
                    {t('app_promo.continue')}
                </button>
            </div>
        </div>
    );
};

export default AppPromoModal;
